import React, { useEffect, useRef, useState } from 'react';
import { Redirect, Route } from 'react-router-dom';
import {
    IonApp,
    IonRouterOutlet,
    IonContent,
    IonAlert,
} from '@ionic/react';

import { IonReactRouter } from '@ionic/react-router';
import Categories from './pages/Categories';
import EnterEmail from './pages/EnterEmail';
import OrderConfirmation from './pages/OrderConfirmation';
import MenuItem from './pages/MenuItem';
import BasketPage from './pages/BasketPage';
import UpsellPage from './pages/UpsellPage';
import MopFooter from './components/MopFooter';
import AppHeader from './components/AppHeader';
import CheckoutTestPage from './pages/CheckoutTestPage';
import CouponsTestPage from './pages/CouponsTestPage';
import PaymentSuccess from './pages/PaymentSuccess';
import HistoryListener from './components/HistoryListener';
import LocationListener from './components/LocationListener';
import TimeSlotPage from './pages/TimeSlotPage';
import StoreSearch from './pages/StoreSearch';
import ChooseOrderType from './pages/ChooseOrderType';
import TableNumber from './pages/TableNumber';
import NameOrNickname from './pages/NameOrNickname';
import TempDealsPage from './pages/TempDealsPage';
import UpgradePage from './pages/UpgradePage';
import { baseUrl } from './helpers/configHelper';
import { useSelector } from 'react-redux';

import Splash from './pages/Splash';

import { ApplicationState } from './store';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

/* Theme variables */
import './theme/variables.css';
import './theme/common.scss';
import DealSummaryPage from './pages/DealSummaryPage';
import ExternalRedirect from './helpers/ExternalRedirect';

const loadGooglePlaces = () => {
    var tag = document.createElement('script') as any;
    tag.async = true;
    tag.type = "text/javascript";
    tag.onload = () => {
        console.log("GOOGLE PLACES LOADED");
    };

    tag.src = "https://maps.googleapis.com/maps/api/js?key=" + process.env.REACT_APP_GOOGLE_LOCATE_KEY + "&libraries=places";
    var body = document.getElementsByTagName('body')[0];
    body.appendChild(tag);
}

const loadHeadTags = () => {
    const favicon = document.createElement('link');
    favicon.rel = "icon";
    favicon.type = "image/png";
    favicon.href = baseUrl() + "/images/icon/cafe/favicon.png";

    const icon = document.createElement('link');
    icon.rel = "apple-touch-icon";
    icon.href = baseUrl() + "/images/icon/cafe/icon.png";

    const head = document.getElementsByTagName('head')[0];

    head.appendChild(favicon);
    head.appendChild(icon);
}

const createManifest = () => {
    let manifest = {
        "name": "Morrisons Cafe Order & Pay",
        "short_name": "Morrisons Cafe",
        "icons": [
            {
                "src": "images/icon/cafe/favicon.png",
                "sizes": "64x64 32x32 24x24 16x16",
                "type": "image/png",
            },
            {
                "src": "images/icon/cafe/icon.png",
                "sizes": "512x512",
                "type": "image/png",
                "purpose": "maskable",
            },
        ],
        "start_url": ".",
        "theme_color": "#ffffff",
        "background_color": "#ffffff",
        "display": "standalone",
    };

    const stringManifest = JSON.stringify(manifest);
    const blob = new Blob([stringManifest], { type: "application/json" });
    const manifestUrl = URL.createObjectURL(blob);

    document.querySelector('#manifest-placeholder')?.setAttribute('href', manifestUrl);
}

const App: React.FC = () => {
    useEffect(() => {
        loadGooglePlaces();
        loadHeadTags();
        createManifest();
    }, []);

    const [showExpireModal, setShowExpireModal] = useState<boolean>(false)

    const contentRef = useRef<HTMLIonContentElement>(null);

    const expired = useSelector((state: ApplicationState) => state.colleague?.expired);

    useEffect(() => {
        if(expired){
            setShowExpireModal(true)
        }
    }, [expired])

    return (
        <IonApp id="root-app">
            <IonReactRouter basename={baseUrl()}>
                <AppHeader >
                </AppHeader>

                <IonContent ref={contentRef}>
                    <LocationListener contentRef={contentRef}></LocationListener>

                    <HistoryListener></HistoryListener>

                    {showExpireModal && <IonAlert
                        isOpen={showExpireModal}
                        onDidDismiss={() => { setShowExpireModal(false); }}
                        header={"You are leaving the colleague area"}
                        buttons={[
                            {
                                text: "Ok",
                                handler: () => {
                                    localStorage.removeItem('isLoggedIn');
                                    localStorage.removeItem("pfidentifier");
                                    window.location.reload();
                                }
                            }
                        ]}
                        message={""}
                    />}

                    <IonRouterOutlet className="mainContent" animated={false} mode={"ios"}>
                        <Route path="/categories/:categoryIdentifier?" render={(props) => <Categories useStoreA={true} {...props}></Categories>} exact={true} />
                        <Route path="/categoriesb/:categoryIdentifier?" render={(props) => <Categories useStoreA={false} {...props}></Categories>} exact={true} />
                        <Route path="/menuitem/:menuItemId" component={MenuItem} />
                        <Route path="/payment" component={CheckoutTestPage} exact/>
                        <Route path="/payment/success" component={PaymentSuccess} />
                        <Route path="/basketpage" component={BasketPage} />
                        <Route path="/temptyou" component={UpsellPage} />
                        <Route path="/orderconfirmation" component={OrderConfirmation} />
                        <Route path="/enteremail" component={EnterEmail} />
                        <Route path="/choosetimeslot" component={TimeSlotPage} />
                        <Route path="/storesearch" component={StoreSearch} />
                        <Route path="/splash" component={Splash} exact={true} />
                        <Route path="/ordertype" component={ChooseOrderType} />
                        <Route path="/tablenumber" component={TableNumber} />
                        <Route path="/name-or-nickname" component={NameOrNickname} />
                        <Route path="/couponz" component={CouponsTestPage} exact />
                        <Route path="/deals" component={TempDealsPage} />
                        <Route path="/deals/upgrade" component={UpgradePage} />
                        <Route path="/deal/:menuItemId" component={MenuItem} />
                        <Route path="/deal-summary" component={DealSummaryPage} />
                        <Route
                            path="/cafe"
                            render={({ location }) => {
                                return <Redirect to={{ pathname:"/splash", search: location.search }} />;
                            }}
                        />
                        <Route
                            path="/colleague"
                            render={({ location }) => {
                                return <Redirect to={{ pathname:"/splash", search: location.search }} />;
                            }}
                        />
                        <Route
                            path="/"
                            exact
                            render={({ location }) => {
                                return <ExternalRedirect to="https://my.morrisons.com/morrisons-cafe/" localhostOverride='/splash' />;
                            }}
                        />
                    </IonRouterOutlet>

                </IonContent>

                <MopFooter>
                </MopFooter>

            </IonReactRouter>
        </IonApp>
    )
};

export default App;
